import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * SHAR-1076/1084 - list filters live in the page address, so "Back" from a
 * detail page, a reload or a pasted link brings them back.
 *
 * Several changes made in one handler (a counter click clears four toggles and
 * sets a status) are merged and written in ONE navigation. setSearchParams'
 * functional updater receives the router's current params, not a queued value,
 * so two separate writes in the same tick would lose the first (SHAR-848).
 * An empty value (null, undefined, '') removes the parameter. `replace` writes
 * over the current history entry (typing a search should not add one per key).
 */
export function useUrlParams(opts: { replace?: boolean } = {}) {
  const [params, setSearchParams] = useSearchParams();
  const pending = useRef<Record<string, string | null | undefined> | null>(null);
  const setParams = useCallback(
    (patch: Record<string, string | null | undefined>) => {
      const first = pending.current === null;
      pending.current = { ...(pending.current ?? {}), ...patch };
      if (!first) return;
      queueMicrotask(() => {
        const p = pending.current ?? {};
        pending.current = null;
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(p)) {
            if (v === null || v === undefined || v === '') next.delete(k);
            else next.set(k, v);
          }
          return next;
        }, { replace: opts.replace });
      });
    },
    [setSearchParams, opts.replace],
  );
  return [params, setParams] as const;
}
